/**
 * Wiki tag matching — one implementation, shared by every surface that filters
 * wiki pages by tag.
 *
 * `getWikiPages` returns tags as a MySQL `group_concat(c.Name)` string, so a
 * page's `Tags` looks like `"payment,sms-template,billing"`. Two things follow:
 *
 *   1. `Tags.includes('payment')` is a SUBSTRING test, not a tag test. It also
 *      matches `prepayment`, `payment-plan` and `payment-reminder` — which is
 *      how the Collect Payment picker could list templates nobody tagged for it.
 *      (Same class of bug the FormsUpdateReminderSettings helper warns about:
 *      `form` matching `platform`.) Split on the comma and compare exactly.
 *   2. `Tags` is null for a page with no tags at all, so guard before splitting.
 *
 * Intentionally pure and IMPORT-FREE — the node-tests load and execute this
 * exact file (see cypress/node-tests/payment-settings-template-filters.test.js)
 * rather than mirroring it, so the guarantee that two pickers agree is tested
 * against the real code. Keep it dependency-free.
 */

/**
 * Exact, comma-split tag membership.
 *
 * @param {{Tags?: string}} page  a wiki page row from `getWikiPages`
 * @param {string} tag            the tag name to look for
 * @returns {boolean}
 */
export function hasTag(page, tag) {
  if (!page || !page.Tags) return false;
  return String(page.Tags).split(',').map(t => t.trim()).includes(tag);
}

/**
 * Every tag on a page, trimmed and blank-free.
 *
 * @param {{Tags?: string}} page
 * @returns {string[]}
 */
export function tagsOf(page) {
  if (!page || !page.Tags) return [];
  return String(page.Tags).split(',').map(t => t.trim()).filter(Boolean);
}

/**
 * The canonical "which wiki pages may be used to text a payment link" filter.
 *
 * Settings → Payments ("Default SMS Template") and the Collect Payment modal
 * MUST offer the same set: a default the send surface cannot list is a setting
 * that can never fire. Both call this.
 *
 * @param {Array} pages  wiki pages from `getWikiPages`
 * @returns {Array}
 */
export function paymentSmsTemplatesFrom(pages) {
  return (pages || []).filter(p => hasTag(p, 'payment'));
}

/**
 * Drop Reviews templates (tagged `review-request`) from a GENERAL template picker.
 *
 * Their {{review_link}} / {{first_name}} / {{practice_name}} are filled ONLY by the Reviews send
 * path (reviews-service reviews-worker.js). Sent from a workflow, Conversations, a campaign or
 * the patient sidebar they reach the patient verbatim, braces and all — so the Wiki editor
 * accepts those variables only on review-request pages (utils/templateVariables), and every
 * general picker must call this. Reviews → Settings is the one place they belong.
 *
 * @param {Array} pages  wiki pages from `getWikiPages`
 * @returns {Array}
 */
export function withoutReviewRequestTemplates(pages) {
  return (pages || []).filter(p => !hasTag(p, 'review-request'));
}

/**
 * Payment EMAIL templates — tagged `payment` AND `email-template`.
 *
 * @param {Array} pages
 * @returns {Array}
 */
export function paymentEmailTemplatesFrom(pages) {
  return (pages || []).filter(p => hasTag(p, 'payment') && hasTag(p, 'email-template'));
}
